import clsx from "clsx";
import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { MetaTagHeader, LoadingAnimation } from "@components";

/**
 * ===============================
 * Dashboard for Project Analysis
 * ===============================
 *
 * This component shows the PA dashboard inside an iframe.
 * It handles loading states, user authentication,
 * and passes user and location info to the iframe URL.
 */
export default function Dashboard() {
    /**
     * === State & References ===
     * loading: boolean, true while iframe content is loading
     * token: string, authentication token retrieved from local storage
     * iframeRef: reference to the iframe DOM element for direct updates
     */
    const [loading, setLoading] = useState(true);
    const [token, setToken] = useState(localStorage.getItem("token") || "");
    const iframeRef = useRef(null);

    /**
     * === External Hooks ===
     * user: information about the logged-in user from Auth0
     * location: current URL info from react-router
     */
    const { user } = useAuth0();
    const location = useLocation();

    /**
     * === Effect: Sync token state with localStorage ===
     * Runs once on component mount.
     * Updates the token state if it differs from localStorage value.
     */
    useEffect(() => {
        const stored = localStorage.getItem("token");
        if (stored !== token) {
            setToken(stored);
        }
    }, []);

    /**
     * === Effect: Update iframe source URL ===
     * Runs whenever user info, URL location, or token changes.
     * Updates iframe src to include new query parameters.
     */
    useEffect(() => {
        if (iframeRef.current && user && location && token) {
            iframeRef.current.src = iframeSrc;
        }
    }, [user, location, token]);

    /**
     * === Build URL Parameters for iframe ===
     * - Extract parameters from URL hash (after '#')
     * - Create query parameters including:
     *   user info, current path, lat/long coordinates, token
     */
    const params = new URLSearchParams(location.hash.slice(1));
    const queryParams = new URLSearchParams({
        user: user ? JSON.stringify(user) : "",
        pathname: location.pathname || "",
        lat: params.get("lat") || "",
        long: params.get("long") || "",
        token: token || "",
    });

    /**
     * === Compose iframe source URL ===
     * Combines base URL from environment variables
     * with the query parameters as a URL hash.
     */
    const iframeSrc = `${process.env.REACT_APP_PROJECT_ANALYSIS_URL}/explorer#${queryParams.toString()}`;

    /**
     * === Render ===
     * - Shows page metadata header
     * - Displays loading animation while iframe loads
     * - Shows iframe only if user is authenticated (token exists)
     */
    return (
        <>
            {/* Page metadata header */}
            <MetaTagHeader />

            {/* Container for loading animation and iframe */}
            <div className={parentContainer}>
                {/* Show loading animation while iframe content is loading */}
                {loading && <LoadingAnimation />}

                {/* Show iframe when token is available */}
                {token && (
                    <iframe
                        title="dashboard"
                        src={iframeSrc}
                        ref={iframeRef}
                        className={loading ? "" : "h-full w-full"}
                        onLoad={() => setLoading(false)} // Hide loading once iframe loads
                        allow="clipboard-write"
                    />
                )}
            </div>
        </>
    );
}

/**
 * === CSS styles for the iframe container ===
 * Positions container absolutely to fill screen
 * Centers contents with flexbox
 * White background to overlay everything else
 */
const parentContainer = clsx(
    "absolute z-50 flex h-screen w-screen items-center justify-center bg-white",
);
